
import { cn } from '../../utils/cn';
import { reducedMotionCSS } from '../../utils/motion';

type ProgressVariant = 'default' | 'success' | 'warning' | 'error';
type ProgressSize = 'sm' | 'md';

export interface ProgressBarProps {
  value: number;
  max?: number;
  label?: string;
  showValue?: boolean;
  variant?: ProgressVariant;
  size?: ProgressSize;
  className?: string;
}

const variantColors: Record<ProgressVariant, { bar: string; track: string }> = {
  default: { bar: '#002045', track: '#d6e3ff' },
  success: { bar: '#2e7d32', track: '#d3f5d6' },
  warning: { bar: '#ed6c02', track: '#ffddba' },
  error: { bar: '#ba1a1a', track: '#ffdad6' },
};

const sizeStyles: Record<ProgressSize, { height: string; fontSize: string }> = {
  sm: { height: '8px', fontSize: '0.75rem' },
  md: { height: '12px', fontSize: '0.8125rem' },
};

export function ProgressBar({
  value,
  max = 100,
  label,
  showValue = false,
  variant = 'default',
  size = 'md',
  className,
}: ProgressBarProps): JSX.Element {
  const clampedValue = Math.max(0, Math.min(value, max));
  const percentage = max > 0 ? Math.round((clampedValue / max) * 100) : 0;
  const colors = variantColors[variant];
  const dims = sizeStyles[size];

  return (
    <div className={cn('ds-progress', className)} style={{ width: '100%' }}>
      {(label || showValue) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.25rem',
          }}
        >
          {label && (
            <span style={{ fontSize: '0.8125rem', color: '#43474e', fontWeight: 500 }}>
              {label}
            </span>
          )}
          {showValue && (
            <span style={{ fontSize: '0.75rem', color: '#43474e' }}>
              {percentage}%
            </span>
          )}
        </div>
      )}

      <div
        role="progressbar"
        aria-valuenow={clampedValue}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label ?? `Progresso: ${percentage}%`}
        style={{
          width: '100%',
          height: dims.height,
          background: colors.track,
          borderRadius: '999px',
          overflow: 'hidden',
        }}
      >
        <div
          className="ds-progress__bar"
          style={{
            width: `${percentage}%`,
            height: '100%',
            background: colors.bar,
            borderRadius: '999px',
            transition: 'width 0.3s ease',
            minWidth: percentage > 0 ? '4px' : '0',
          }}
        />
      </div>

      <style>{`
        .ds-progress__bar {
          animation: ds-progress-fill 0.3s ease;
        }
        ${reducedMotionCSS('.ds-progress__bar')}
      `}</style>
    </div>
  );
}

ProgressBar.displayName = 'ProgressBar';
